import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Film, Trash2, AlertCircle, ArrowLeft, Popcorn, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getWatchlist, removeFromWatchlist } from '../../lib/supabase';
import { WatchedButton } from '../ui/WatchedButton';
import { MovieListControls } from '../ui/MovieListControls';
import { sortMovies, paginateMovies } from '../../lib/sorting';
import type { MovieListConfig } from '../../lib/types';

interface WatchlistMovie {
  id: string;
  tmdb_id: number;
  title: string;
  year: number;
  poster_url: string | null;
  overview: string;
  created_at: string;
}

export function WatchlistPage() {
  const navigate = useNavigate();
  const [movies, setMovies] = useState<WatchlistMovie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [listConfig, setListConfig] = useState<MovieListConfig>({
    sort: { field: 'added', order: 'desc' },
    pagination: { page: 1, perPage: 10, total: 0 }
  });

  useEffect(() => {
    loadWatchlist();
  }, []);

  const loadWatchlist = async () => {
    try {
      setLoading(true);
      setError(null);
      const watchlist = await getWatchlist();
      setMovies(watchlist);
      setListConfig(prev => ({
        ...prev,
        pagination: { ...prev.pagination, total: watchlist.length }
      }));
    } catch (err) {
      console.error('Error loading watchlist:', err);
      setError('Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (tmdbId: number) => {
    try {
      setRemovingId(tmdbId);
      await removeFromWatchlist(tmdbId);
      setMovies(prev => {
        const updated = prev.filter(movie => movie.tmdb_id !== tmdbId);
        setListConfig(config => ({
          ...config,
          pagination: {
            ...config.pagination,
            total: updated.length,
            page: Math.min(
              config.pagination.page,
              Math.ceil(updated.length / config.pagination.perPage)
            )
          }
        }));
        return updated;
      });
    } catch (err) {
      console.error('Error removing from watchlist:', err);
      setError('Failed to remove movie from watchlist');
    } finally {
      setRemovingId(null);
    }
  };

  const sortedMovies = sortMovies(movies, listConfig.sort);
  const paginatedMovies = paginateMovies(
    sortedMovies,
    listConfig.pagination.page,
    listConfig.pagination.perPage
  );

  if (loading) {
    return (
      <div className="grain flex min-h-screen items-center justify-center">
        <div className="h-1 w-40 overflow-hidden rounded-full bg-secondary">
          <div className="animate-progress h-full rounded-full bg-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen">
      <header className="app-header px-4 pb-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="btn-quiet p-2"
              aria-label="Back to home"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="display flex items-center gap-2.5 whitespace-nowrap text-xl font-semibold sm:text-2xl">
              <span
                className="hidden h-8 w-8 items-center justify-center rounded-full text-primary-foreground shadow-glow-gold-soft sm:flex"
                style={{ background: 'linear-gradient(180deg, hsl(44 97% 62%), hsl(35 92% 49%))' }}
              >
                <Popcorn className="h-4 w-4" />
              </span>
              My Watchlist
            </h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            <button
              onClick={() => navigate('/conversation')}
              className="btn-quiet px-2.5 py-2 text-sm"
              aria-label="Get Recommendations"
            >
              <Sparkles className="h-4 w-4 sm:hidden" />
              <span className="hidden sm:inline">Get Recommendations</span>
            </button>
            <button
              onClick={() => navigate('/watched')}
              className="btn-quiet px-2.5 py-2 text-sm"
              aria-label="Watched Movies"
            >
              <Film className="h-4 w-4 sm:hidden" />
              <span className="hidden sm:inline">Watched Movies</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10 md:py-12">
        {error && (
          <div className="mb-8 flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/10 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-400" />
            <p className="text-red-300">{error}</p>
          </div>
        )}

        {movies.length > 0 ? (
          <>
            <MovieListControls
              sort={listConfig.sort}
              pagination={listConfig.pagination}
              onSortChange={(field, order) => setListConfig(prev => ({
                ...prev,
                sort: { field, order }
              }))}
              onPageChange={page => setListConfig(prev => ({
                ...prev,
                pagination: { ...prev.pagination, page }
              }))}
              className="mb-5"
            />

            <div className="space-y-3.5">
              {paginatedMovies.map((movie, i) => (
                <div
                  key={movie.id}
                  className="panel-raised accent-frame animate-rise flex gap-5 p-5"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <div className="poster-frame h-36 w-24 flex-shrink-0 shadow-poster">
                    {movie.poster_url ? (
                      <img
                        src={movie.poster_url}
                        alt={movie.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Film className="h-8 w-8 text-muted-foreground/40" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2 className="display text-lg font-semibold leading-snug sm:text-xl">
                          {movie.title}
                        </h2>
                        <p className="text-sm text-muted-foreground">{movie.year}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <WatchedButton
                          tmdbId={movie.tmdb_id}
                          title={movie.title}
                          year={movie.year}
                          posterUrl={movie.poster_url}
                          overview={movie.overview}
                        />
                        <button
                          onClick={() => handleRemove(movie.tmdb_id)}
                          disabled={removingId === movie.tmdb_id}
                          aria-label={`Remove ${movie.title} from watchlist`}
                          className={cn(
                            'rounded-full border border-white/10 bg-white/[0.03] p-2.5 text-muted-foreground',
                            'transition-all duration-200 hover:border-destructive/40 hover:text-red-400',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive',
                            removingId === movie.tmdb_id && 'cursor-not-allowed opacity-50'
                          )}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground sm:line-clamp-3">
                      {movie.overview}
                    </p>
                    <p className="mt-2.5 text-xs tracking-wide text-muted-foreground/60">
                      Added {new Date(movie.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="panel mx-auto max-w-lg px-8 py-14 text-center">
            <div className="mx-auto mb-5 inline-flex rounded-2xl border border-primary/25 bg-primary/10 p-4 text-primary">
              <Popcorn className="h-8 w-8" />
            </div>
            <h2 className="display mb-2 text-2xl font-semibold">
              Your watchlist is empty
            </h2>
            <p className="mx-auto mb-7 max-w-sm leading-relaxed text-muted-foreground">
              Start adding movies to your watchlist to keep track of what you want to watch.
            </p>
            <button
              onClick={() => navigate('/conversation')}
              className="btn-marquee px-7 py-3"
            >
              <Sparkles className="h-5 w-5" />
              Get Movie Recommendations
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
